import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  TrendingUp, DollarSign, Clock, 
  CheckCircle, XCircle, Loader2, Settings, Save, Users, FileCheck, Eye, ShieldCheck, Gift, Archive, RotateCcw, Pencil
} from 'lucide-react';
import { useAdminAuth } from '../context/AdminAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminInvestments = () => {
  const [investments, setInvestments] = useState([]);
  const [investors, setInvestors] = useState([]);
  const [investorStats, setInvestorStats] = useState({});
  const [kycSubmissions, setKycSubmissions] = useState([]);
  const [kycPreview, setKycPreview] = useState(null);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [plans, setPlans] = useState([]);
  const [savingSettings, setSavingSettings] = useState(false);
  const [activeTab, setActiveTab] = useState('investments');
  const [referralSettings, setReferralSettings] = useState({ enabled: true, bonus_type: 'fixed', bonus_amount: 0 });
  const [showReferralSettings, setShowReferralSettings] = useState(false);
  const [showWithdrawals, setShowWithdrawals] = useState(false);
  const [withdrawals, setWithdrawals] = useState([]);
  const { authenticated, currentUser, loading: authLoading, logout, getAuthHeader } = useAdminAuth();
  const isSuperAdmin = currentUser?.role === 'super_admin';
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && !authenticated) {
      navigate('/admin/login');
    }
  }, [authenticated, authLoading, navigate]);

  useEffect(() => {
    if (authenticated) {
      fetchInvestments();
      fetchSettings();
      fetchInvestors();
      fetchKycSubmissions();
      fetchReferralSettings();
    }
  }, [authenticated]);

  const fetchReferralSettings = async () => { try { const res = await axios.get(`${API}/admin/referral-program`, { headers: getAuthHeader() }); setReferralSettings(res.data.settings); } catch { /* settings stay at safe defaults */ } };
  const fetchWithdrawals = async () => { try { const res = await axios.get(`${API}/admin/bonus-withdrawals`, { headers: getAuthHeader() }); setWithdrawals(res.data.requests || []); } catch (error) { toast.error(error.response?.data?.detail || 'Failed to load withdrawal requests'); } };
  const decideWithdrawal = async (row, decision) => { const reason = decision === 'rejected' ? window.prompt('Rejection reason') : ''; if (decision === 'rejected' && reason === null) return; try { await axios.post(`${API}/admin/bonus-withdrawals/${row._id}/decision`, { decision, reason }, { headers: getAuthHeader() }); toast.success(`Withdrawal ${decision}`); fetchWithdrawals(); } catch (error) { toast.error(error.response?.data?.detail || 'Could not update withdrawal'); } };
  const saveReferralSettings = async () => { try { await axios.put(`${API}/admin/referral-program`, referralSettings, { headers: getAuthHeader() }); toast.success('Referral bonus settings saved'); setShowReferralSettings(false); } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save referral settings'); } };
  const addBonus = async (inv) => { const amount = window.prompt('Bonus amount in naira'); if (!amount || Number(amount) <= 0) return; const destination = window.confirm('Add to the investment payout? Choose Cancel to credit the separate bonus wallet.') ? 'investment' : 'wallet'; try { await axios.post(`${API}/admin/investments/${inv._id}/bonus`, { amount: Number(amount), destination }, { headers: getAuthHeader() }); toast.success('Bonus added'); fetchInvestments(); } catch (error) { toast.error(error.response?.data?.detail || 'Failed to add bonus'); } };
  const archiveInvestment = async (inv) => { if (!window.confirm('Archive this investment? It can be restored later and remains in the audit history.')) return; try { await axios.delete(`${API}/admin/investments/${inv._id}`, { headers: getAuthHeader(), data: { reason: 'Archived by Super Admin', status_before_archive: inv.status } }); toast.success('Investment archived'); fetchInvestments(); } catch (error) { toast.error(error.response?.data?.detail || 'Failed to archive investment'); } };
  const restoreInvestment = async (inv) => { try { await axios.post(`${API}/admin/investments/${inv._id}/restore`, {}, { headers: getAuthHeader() }); toast.success('Investment restored'); fetchInvestments(); } catch (error) { toast.error(error.response?.data?.detail || 'Failed to restore investment'); } };
  const editInvestmentAmount = async (inv) => { const amount = window.prompt('New investment amount in naira', inv.amount); if (!amount || Number(amount) <= 0) return; try { await axios.put(`${API}/admin/investments/${inv._id}`, { amount: Number(amount), return_percentage: inv.return_percentage }, { headers: getAuthHeader() }); toast.success('Investment amount updated'); fetchInvestments(); } catch (error) { toast.error(error.response?.data?.detail || 'Failed to edit investment'); } };

  const fetchInvestments = async () => {
    try {
      const response = await axios.get(`${API}/admin/investments`, {
        headers: getAuthHeader()
      });
      setInvestments(response.data.investments || []);
      setStats(response.data.stats || {});
    } catch (error) {
      toast.error('Failed to load investments');
    } finally {
      setLoading(false);
    }
  };

  const fetchInvestors = async () => {
    try {
      const response = await axios.get(`${API}/admin/investors`, {
        headers: getAuthHeader()
      });
      setInvestors(response.data.investors || []);
      setInvestorStats(response.data.stats || {});
    } catch (error) {
      console.error('Failed to load investors');
    }
  };

  const fetchKycSubmissions = async () => {
    try {
      const response = await axios.get(`${API}/admin/kyc/pending`, {
        headers: getAuthHeader()
      });
      setKycSubmissions(response.data.submissions || []);
    } catch (error) {
      console.error('Failed to load KYC submissions');
    }
  };

  const reviewKyc = async (userType, userId, status, notes = '') => {
    try {
      await axios.put(`${API}/admin/kyc/${userType}/${userId}`, { status, notes }, {
        headers: getAuthHeader()
      });
      toast.success(`KYC ${status}`);
      fetchKycSubmissions();
      fetchInvestors();
    } catch (error) {
      toast.error('Failed to update KYC');
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/investments/settings`, {
        headers: getAuthHeader()
      });
      setPlans(response.data.plans || []);
    } catch (error) {
      console.error('Failed to load settings');
    }
  };

  const updateInvestment = async (investmentId, data) => {
    try {
      await axios.put(`${API}/admin/investments/${investmentId}`, data, {
        headers: getAuthHeader()
      });
      toast.success('Investment updated');
      fetchInvestments();
    } catch (error) {
      toast.error('Failed to update investment');
    }
  };

  const confirmInvestmentPayment = async (investmentId) => {
    if (!window.confirm('Mark this payment as received? The investment will activate and the investor will get the approval email.')) return;
    try {
      await axios.post(`${API}/admin/investments/${investmentId}/confirm-payment`, {}, {
        headers: getAuthHeader()
      });
      toast.success('Payment confirmed — investment activated');
      fetchInvestments();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to confirm payment');
    }
  };

  const updateInvestor = async (investorId, data) => {
    try {
      await axios.put(`${API}/admin/investors/${investorId}`, data, {
        headers: getAuthHeader()
      });
      toast.success('Investor updated');
      fetchInvestors();
    } catch (error) {
      toast.error('Failed to update investor');
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/admin/investments/settings`, { plans }, {
        headers: getAuthHeader()
      });
      toast.success('Settings saved');
      setShowSettings(false);
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const updatePlan = (index, field, value) => {
    const newPlans = [...plans];
    newPlans[index] = { ...newPlans[index], [field]: value };
    setPlans(newPlans);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'approved': return 'bg-blue-100 text-blue-700';
      case 'active': return 'bg-green-100 text-green-700';
      case 'completed': return 'bg-gray-100 text-gray-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (authLoading || !authenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-green-600" />
      </div>
    );
  }

  return (
    <div data-testid="admin-investments-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold">Investment Management</h1>
          <p className="text-sm text-gray-500">Review applications and manage plans</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setShowSettings(true)}>
          <Settings className="h-4 w-4 mr-2" /> Plan Settings
        </Button>
        {isSuperAdmin && <Button variant="outline" size="sm" onClick={() => setShowReferralSettings(true)} data-testid="referral-program-settings-button"><Gift className="h-4 w-4 mr-2" />Referral program</Button>}
        {isSuperAdmin && <Button variant="outline" size="sm" onClick={() => { setShowWithdrawals(true); fetchWithdrawals(); }} data-testid="bonus-withdrawals-button">Bonus withdrawals</Button>}
      </div>

      <div>
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Invested</p>
                <p className="text-xl font-bold">₦{stats.total_invested?.toLocaleString() || 0}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Pending</p>
                <p className="text-xl font-bold">{stats.pending_count || 0}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Active</p>
                <p className="text-xl font-bold">{stats.active_count || 0}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <Users className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Applications</p>
                <p className="text-xl font-bold">{stats.total_count || 0}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={activeTab === 'investments' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveTab('investments')}
            data-testid="tab-investments"
          >
            <TrendingUp className="h-4 w-4 mr-2" /> Investments ({stats.total_count || 0})
          </Button>
          <Button
            variant={activeTab === 'investors' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveTab('investors')}
            data-testid="tab-investors"
          >
            <Users className="h-4 w-4 mr-2" /> Investors ({investorStats.total_investors || 0})
          </Button>
          <Button
            variant={activeTab === 'kyc' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveTab('kyc')}
            data-testid="tab-kyc"
          >
            <ShieldCheck className="h-4 w-4 mr-2" /> KYC ({kycSubmissions.length})
          </Button>
        </div>

        {/* Investments Tab */}
        {activeTab === 'investments' && (
        <Card>
          <CardHeader>
            <CardTitle>Investment Applications</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-12 text-center">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-gray-400" />
              </div>
            ) : investments.length === 0 ? (
              <div className="py-12 text-center text-gray-500">
                No investment applications yet
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left p-3 font-semibold">Application #</th>
                      <th className="text-left p-3 font-semibold">Customer</th>
                      <th className="text-left p-3 font-semibold">Plan</th>
                      <th className="text-right p-3 font-semibold">Amount</th>
                      <th className="text-right p-3 font-semibold">Expected Return</th>
                      <th className="text-center p-3 font-semibold">Status</th>
                      <th className="text-center p-3 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {investments.map((inv) => (
                      <tr key={inv._id} className="border-b hover:bg-gray-50">
                        <td className="p-3">
                          <p className="font-mono text-sm">{inv.application_number}</p>
                          <p className="text-xs text-gray-500">{new Date(inv.created_at).toLocaleDateString()}</p>
                        </td>
                        <td className="p-3">
                          <p className="font-semibold">{inv.full_name}</p>
                          <p className="text-sm text-gray-500">{inv.email}</p>
                        </td>
                        <td className="p-3">
                          <p>{inv.plan_name}</p>
                          <p className="text-sm text-gray-500">{inv.duration_months} months @ {inv.return_percentage}%</p>
                        </td>
                        <td className="p-3 text-right font-semibold">
                          ₦{inv.amount?.toLocaleString()}
                        </td>
                        <td className="p-3 text-right text-green-600 font-semibold">
                          ₦{inv.expected_return?.toLocaleString()}
                        </td>
                        <td className="p-3 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(inv.status)}`}>
                            {inv.status}
                          </span>
                        </td>
                        <td className="p-3">
                          <div className="flex justify-center gap-1">
                            {isSuperAdmin && <>
                              <Button size="sm" variant="outline" onClick={() => editInvestmentAmount(inv)} data-testid={`edit-investment-${inv._id}`} title="Edit investment amount"><Pencil className="h-4 w-4" /></Button>
                              <Button size="sm" variant="outline" onClick={() => addBonus(inv)} data-testid={`add-investment-bonus-${inv._id}`} title="Add bonus"><Gift className="h-4 w-4" /></Button>
                              {inv.archived_at ? <Button size="sm" variant="outline" onClick={() => restoreInvestment(inv)} data-testid={`restore-investment-${inv._id}`} title="Restore investment"><RotateCcw className="h-4 w-4" /></Button> : <Button size="sm" variant="outline" className="text-red-600" onClick={() => archiveInvestment(inv)} data-testid={`archive-investment-${inv._id}`} title="Archive investment"><Archive className="h-4 w-4" /></Button>}
                            </>}
                            {(inv.status === 'pending' || inv.status === 'pending_payment') && (
                              <>
                                <Button
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700 text-white"
                                  onClick={() => confirmInvestmentPayment(inv._id)}
                                  data-testid={`confirm-payment-btn-${inv._id}`}
                                  title="Confirm payment received — activates investment + emails investor"
                                >
                                  <CheckCircle className="h-4 w-4 mr-1" /> Confirm Payment
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600"
                                  onClick={() => updateInvestment(inv._id, { status: 'rejected' })}
                                >
                                  <XCircle className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                            {inv.status === 'approved' && (
                              <Button
                                size="sm"
                                onClick={() => updateInvestment(inv._id, { status: 'active', payment_status: 'paid' })}
                              >
                                Activate
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {/* Investors Tab */}
        {activeTab === 'investors' && (
          <Card>
            <CardHeader>
              <CardTitle>Registered Investors</CardTitle>
            </CardHeader>
            <CardContent>
              {investors.length === 0 ? (
                <div className="py-12 text-center text-gray-500">
                  No registered investors yet
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left p-3 font-semibold">Investor</th>
                        <th className="text-left p-3 font-semibold">Contact</th>
                        <th className="text-left p-3 font-semibold">Bank Details</th>
                        <th className="text-right p-3 font-semibold">Investments</th>
                        <th className="text-right p-3 font-semibold">Total Invested</th>
                        <th className="text-center p-3 font-semibold">Status</th>
                        <th className="text-center p-3 font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {investors.map((inv) => (
                        <tr key={inv._id} className="border-b hover:bg-gray-50">
                          <td className="p-3">
                            <p className="font-semibold">{inv.full_name}</p>
                            <p className="text-xs text-gray-500">Joined {new Date(inv.created_at).toLocaleDateString()}</p>
                          </td>
                          <td className="p-3">
                            <p className="text-sm">{inv.email}</p>
                            <p className="text-sm text-gray-500">{inv.phone || '-'}</p>
                          </td>
                          <td className="p-3">
                            <p className="text-sm">{inv.bank_name || 'Not set'}</p>
                            <p className="text-xs text-gray-500">{inv.account_number ? `****${inv.account_number.slice(-4)}` : '-'}</p>
                          </td>
                          <td className="p-3 text-right">
                            <p className="font-semibold">{inv.investment_count || 0}</p>
                            <p className="text-xs text-gray-500">{inv.active_investments_actual || 0} active</p>
                          </td>
                          <td className="p-3 text-right font-semibold">
                            ₦{(inv.total_invested_actual || 0).toLocaleString()}
                          </td>
                          <td className="p-3 text-center">
                            <div className="flex flex-col items-center gap-1">
                              <span className={`px-2 py-0.5 rounded-full text-xs ${inv.is_verified ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                {inv.is_verified ? 'Verified' : 'Unverified'}
                              </span>
                              <span className={`px-2 py-0.5 rounded-full text-xs ${inv.is_active ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'}`}>
                                {inv.is_active ? 'Active' : 'Disabled'}
                              </span>
                            </div>
                          </td>
                          <td className="p-3">
                            <div className="flex justify-center gap-1">
                              {!inv.is_verified && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-green-600"
                                  onClick={() => updateInvestor(inv._id, { is_verified: true })}
                                  data-testid={`verify-investor-${inv._id}`}
                                >
                                  <CheckCircle className="h-4 w-4" />
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                className={inv.is_active ? 'text-red-600' : 'text-green-600'}
                                onClick={() => updateInvestor(inv._id, { is_active: !inv.is_active })}
                              >
                                {inv.is_active ? <XCircle className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}
        {/* KYC Tab */}
        {activeTab === 'kyc' && (
          <Card>
            <CardHeader>
              <CardTitle>KYC Submissions</CardTitle>
            </CardHeader>
            <CardContent>
              {kycSubmissions.length === 0 ? (
                <div className="py-8 text-center text-gray-500">No pending KYC submissions</div>
              ) : (
                <div className="space-y-4">
                  {kycSubmissions.map((sub) => (
                    <div key={sub._id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <p className="font-semibold">{sub.full_name}</p>
                          <p className="text-sm text-gray-500">{sub.email} ({sub.user_type})</p>
                          <p className="text-xs text-gray-400">Submitted {new Date(sub.kyc?.submitted_at).toLocaleDateString()}</p>
                        </div>
                        <span className={`px-2 py-0.5 rounded-full text-xs ${
                          sub.kyc?.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                          sub.kyc?.status === 'under_review' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {sub.kyc?.status}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-4 mb-3">
                        {sub.kyc?.id_front && (
                          <div>
                            <p className="text-xs font-medium text-gray-500 mb-1">ID Front</p>
                            <img src={`${BACKEND_URL}${sub.kyc.id_front}`} alt="ID Front" className="w-full h-40 object-cover rounded border cursor-pointer hover:opacity-80" onClick={() => setKycPreview(`${BACKEND_URL}${sub.kyc.id_front}`)} />
                          </div>
                        )}
                        {sub.kyc?.id_back && (
                          <div>
                            <p className="text-xs font-medium text-gray-500 mb-1">ID Back</p>
                            <img src={`${BACKEND_URL}${sub.kyc.id_back}`} alt="ID Back" className="w-full h-40 object-cover rounded border cursor-pointer hover:opacity-80" onClick={() => setKycPreview(`${BACKEND_URL}${sub.kyc.id_back}`)} />
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => reviewKyc(sub.user_type, sub._id, 'approved')} data-testid={`approve-kyc-${sub._id}`}>
                          <CheckCircle className="h-4 w-4 mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => reviewKyc(sub.user_type, sub._id, 'rejected', 'Document unclear or expired')} data-testid={`reject-kyc-${sub._id}`}>
                          <XCircle className="h-4 w-4 mr-1" /> Reject
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => reviewKyc(sub.user_type, sub._id, 'under_review')}>
                          <Eye className="h-4 w-4 mr-1" /> Under Review
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* KYC Preview Modal */}
        {kycPreview && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setKycPreview(null)}>
            <img src={kycPreview} alt="KYC Document" className="max-w-full max-h-[85vh] rounded-lg" />
          </div>
        )}
      </div>

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>Investment Plan Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {plans.map((plan, idx) => (
                <div key={plan.id} className="p-4 border rounded-lg">
                  <h4 className="font-semibold mb-3">{plan.name}</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Return Percentage</label>
                      <Input
                        type="number"
                        value={plan.return_percentage}
                        onChange={(e) => updatePlan(idx, 'return_percentage', parseInt(e.target.value))}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Duration (Months)</label>
                      <Input
                        type="number"
                        value={plan.duration_months}
                        onChange={(e) => updatePlan(idx, 'duration_months', parseInt(e.target.value))}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Minimum Amount (₦)</label>
                      <Input
                        type="number"
                        value={plan.min_amount}
                        onChange={(e) => updatePlan(idx, 'min_amount', parseInt(e.target.value))}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Maximum Amount (₦)</label>
                      <Input
                        type="number"
                        value={plan.max_amount}
                        onChange={(e) => updatePlan(idx, 'max_amount', parseInt(e.target.value))}
                      />
                    </div>
                  </div>
                </div>
              ))}
              
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setShowSettings(false)} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={saveSettings} className="flex-1 bg-green-600 hover:bg-green-700" disabled={savingSettings}>
                  {savingSettings && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  <Save className="h-4 w-4 mr-2" /> Save Settings
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      {showReferralSettings && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="referral-program-modal">
          <Card className="w-full max-w-md"><CardHeader><CardTitle>Referral bonus program</CardTitle></CardHeader><CardContent className="space-y-4">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={referralSettings.enabled} onChange={(e) => setReferralSettings({ ...referralSettings, enabled: e.target.checked })} data-testid="referral-enabled-checkbox" /> Enable rewards after confirmed payment</label>
            <div><label className="text-sm font-medium">Reward method</label><select className="w-full border rounded-md h-10 px-3 mt-1" value={referralSettings.bonus_type} onChange={(e) => setReferralSettings({ ...referralSettings, bonus_type: e.target.value })} data-testid="referral-bonus-type"><option value="fixed">Fixed naira amount</option><option value="percentage">Percentage of investment</option></select></div>
            <div><label className="text-sm font-medium">{referralSettings.bonus_type === 'fixed' ? 'Amount (₦)' : 'Percentage (%)'}</label><Input type="number" min="0" max={referralSettings.bonus_type === 'percentage' ? 100 : undefined} value={referralSettings.bonus_amount} onChange={(e) => setReferralSettings({ ...referralSettings, bonus_amount: e.target.value })} data-testid="referral-bonus-amount" /></div>
            <div className="flex gap-2"><Button variant="outline" className="flex-1" onClick={() => setShowReferralSettings(false)} data-testid="cancel-referral-settings">Cancel</Button><Button className="flex-1" onClick={saveReferralSettings} data-testid="save-referral-settings">Save program</Button></div>
          </CardContent></Card>
        </div>
      )}
      {showWithdrawals && <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="bonus-withdrawals-modal"><Card className="w-full max-w-2xl max-h-[80vh] overflow-y-auto"><CardHeader><CardTitle>Pending bonus withdrawals</CardTitle></CardHeader><CardContent className="space-y-3">{withdrawals.length === 0 ? <p className="py-8 text-center text-gray-500" data-testid="no-bonus-withdrawals">No pending bonus withdrawals.</p> : withdrawals.map((row) => <div className="border rounded p-3 flex flex-wrap justify-between gap-3" key={row._id} data-testid={`bonus-withdrawal-${row._id}`}><div><p className="font-medium">{row.email}</p><p className="text-sm text-gray-500">₦{Number(row.amount).toLocaleString()}</p></div><div className="flex gap-2"><Button size="sm" onClick={() => decideWithdrawal(row, 'approved')} data-testid={`approve-bonus-withdrawal-${row._id}`}>Approve</Button><Button size="sm" variant="destructive" onClick={() => decideWithdrawal(row, 'rejected')} data-testid={`reject-bonus-withdrawal-${row._id}`}>Reject</Button></div></div>)}<Button variant="outline" className="w-full" onClick={() => setShowWithdrawals(false)} data-testid="close-bonus-withdrawals">Close</Button></CardContent></Card></div>}
    </div>
  );
};

export default AdminInvestments;
